// Chatwoot support-conversation unread state (parent bd-eiw). Fed by an initial
// /chatwoot/conversation-status fetch, live WS `chatwoot_status` pushes, and an
// optimistic reset when the user opens the widget. Drives the sidebar badge.

import { create } from 'zustand'
import { chatwootApi } from '../api/chatwoot'
import type { ChatwootStatus } from '../types/api'

interface ChatwootState {
  hasConversation: boolean
  resolved: boolean
  unread: number
  show: () => boolean
  setStatus: (p: Partial<ChatwootStatus> | undefined) => void
  fetch: () => Promise<void>
  markRead: () => void
  reset: () => void
}

export const useChatwootStore = create<ChatwootState>()((set, get) => ({
  hasConversation: false,
  resolved: false,
  unread: 0,

  // Badge shows only for an open (non-resolved) conversation with unread replies.
  show: () => {
    const s = get()
    return s.hasConversation && !s.resolved && s.unread > 0
  },

  // Accept either the REST shape or the WS payload (same fields).
  setStatus: (p) =>
    set({
      hasConversation: Boolean(p?.has_conversation),
      resolved: Boolean(p?.resolved),
      unread: p?.unread_count || 0,
    }),

  // Pull current state from the backend (init / WS reconnect resync).
  fetch: async () => {
    try {
      const data = await chatwootApi.conversationStatus()
      get().setStatus(data)
    } catch {
      /* best-effort — a later push/poll resyncs */
    }
  },

  // User opened the widget → clear unread optimistically + tell the backend.
  markRead: () => {
    set({ unread: 0 })
    chatwootApi.markRead().catch(() => {})
  },

  reset: () => set({ hasConversation: false, resolved: false, unread: 0 }),
}))
